//!#system	

<template>
	<v-card>
		<v-card-title primary-title>
			<span class="headline">Custom Fields</span>
		</v-card-title>
		<v-divider></v-divider>
		<v-card-text class="pa-6">
			<div v-for="(customField, index) in customFields" :key="customField.name">
				<custom-field-definition v-model="customFields[index]" @remove="removeField" />
			</div>
			<div v-if="hasReachedLimit">
				<v-alert type="error">You have reached the maximum number of custom fields allowed for this subscription.</v-alert>
			</div>
			<div v-if="customFields.length === 0">
				<v-alert type="info">There are no custom fields configured.</v-alert>
			</div>
			<v-tooltip bottom>
				<template v-slot:activator="{ on }">
					<v-btn v-on="on" @click="addCustomField()" color="secondary" :disabled="hasReachedLimit" fab x-small dark>
						<v-icon dark x-small>mdi-plus</v-icon>
					</v-btn>
				</template>
				<span>Add a custom field</span>
			</v-tooltip>
			<v-dialog v-model="newCustomFieldDialog" max-width="400" persistent>
				<v-card>
					<v-card-title>
						<span class="headline">New custom field</span>
					</v-card-title>
					<v-card-text>
						<v-text-field
							v-model="newField.name"
							label="New Custom Field Name"
							:error="!!fieldNameIsInvalid(newField.name)"
							:hint="fieldNameIsInvalid(newField.name) || '`camelCase` naming works best.'"
						/>
						<v-select
							v-model="newField.type"
							label="Field type"
							:items="types"
						/>
						<v-combobox
							v-model="newField.default"
							label="Initial value"
							:items="['Empty/null']"
						/>
						<v-switch
							inset
							v-model="newField.allowNulls"
							label="Allow nulls (empty values)"
						/>
						<v-switch
							inset
							v-model="newField.index"
							label="Index this field"
						/>
						<v-switch
							inset
							v-model="newField.listScreen"
							label="Available on list screens / APIs"
						/>
						<v-combobox
							v-model="newField.valueList"
							label="Value list (optional)"
							:items="[]"
							multiple
							chips
							hint="Hit enter to add an option"
							persistent-hint
						/>
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn text @click="newCustomFieldDialog = false">Exit</v-btn>
						<v-btn color="primary" :disabled="!canSubmitNewFieldForm" @click="insertNewField">Create field</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
			<v-dialog v-model="confirmSaveDialog" max-width="350" persistent>
				<v-card>
					<v-card-title>
						<span class="headline">Are you sure?</span>
					</v-card-title>
					<v-card-text>
						Things to know:
						<ul>
							<li>You cannot delete custom fields after you create them.</li>
							<li>You cannot change their type after you create them.</li>
							<li>You will need to refresh your browser window after saving this screen in order to use your fields.</li>
						</ul>
						Be sure that you set your custom fields up correctly before saving.
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn text @click="confirmSaveDialog = false">Exit</v-btn>
						<v-btn color="primary" @click="doAction" :loading="isLoading">Yes, save these changes</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
		</v-card-text>
		<v-divider></v-divider>
		<v-card-actions>
			<v-spacer></v-spacer>
			<v-btn text @click="closeDialog()">Exit</v-btn>
			<v-btn color="primary" :disabled="!canSubmit" @click="confirmSaveDialog = true">{{submitText}}</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import CustomFieldDefinition from './CustomFieldDefinition';
import apiService from '@/apiService';

const fieldDefinitionDefault = function() {
	return {
		name: '',
		type: 'text',
		default: 'Empty/null',
		enabled: true,
		allowNulls: true,
		listScreen: true,
		index: true,
		valueList: [],
		committed: false
	};
};

export default {
	props: {
		routeInfo: {
			type: Object
		},
		detail: {
			type: Object
		}
	},
	components: { CustomFieldDefinition },
	data() {
		return {
			isLoading: false,
			confirmSaveDialog: false,
			newCustomFieldDialog: false,
			newField: fieldDefinitionDefault(),
			fields: []
		}
	},
	computed: {
		customFields() {
			return this.fields;
		},
		canSubmit() {
			return true;
		},
		submitText() {
			return 'Save';
		},
		types() {
			return [
				'text',
				'large-text',
				'number',
				'decimal',
				'date'
			];
		},
		hasReachedLimit() {
			const subscription = this.$store.state.app.subscription || {};
			const bucketSubscription = subscription['custom/customFieldConfigs'];

			if (bucketSubscription) {
				const limit = bucketSubscription['customFieldsPerBucket'];

				if (limit == 0) {
					return true;
				} else if (!limit) {
					// No limits set
					return false;
				}

				if (limit < this.fields.length) {
					return true;
				} else {
					return false;
				}
			} else {
				// No limits set
				return false;
			}
		},
		canSubmitNewFieldForm() {
			let canSubmit = true;
			if (this.hasReachedLimit) canSubmit = false;
			if (this.fieldNameIsInvalid(this.newField.name)) canSubmit = false;
			return canSubmit;
		}
	},
	methods: {
		doAction() {
			if (this.hasReachedLimit) {
				this.$store.dispatch('app/applyObjectError', {
					show: true,
					text: 'You have reached the maximum number of custom fields allowed for this subscription.'
				});
				return;
			}
			this.isLoading = true;
			apiService.action({
				...this.routeInfo,
				action: 'editFields',
				method: 'post',
				payload: { fields: this.fields }
			}, (err, result) => {
				this.isLoading = false;
				if (err) {
					this.$store.dispatch('app/applyObjectError', {
						show: true,
						text: err
					});
				} else {
					this.$emit('close');
					this.$emit('refresh');
				}
			});
		},
		removeField(field) {
			if (field.committed) return;
			const fieldIndex = this.fields.map(e => e.name).indexOf(field.name);
			this.$delete(this.fields, fieldIndex);
		},
		insertNewField() {
			this.fields.push(this.newField);
			this.newField = fieldDefinitionDefault();
			this.newCustomFieldDialog = false;
		},
		fieldNameIsInvalid(str) {
			if (!str) return 'Make sure you enter a name';
			let s = str.toString();
			if (s.includes(' ') || s.match(/[^\w]/)) return 'Your field name should not include spaces or any invalid characters';
			if (s.includes('_')) return `The "underscore" character ('_') is not allowed in custom field names`;
			return null;
		},
		addCustomField() {
			this.newCustomFieldDialog = true;
		},
		closeDialog() {
			this.$emit('close');
		}
	},
	created() {
		if (!this.detail.configData.fields) {
			this.detail.configData.fields = [];
		}
		this.fields = this.detail.configData.fields;
	}
}
</script>